export function sanitizeClientLogValue(value: string, maxLength = 100) {
  const withoutEmail = value.replace(/[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/g, "[이메일 제거]");
  const withoutBearer = withoutEmail.replace(/Bearer\s+[A-Za-z0-9._~-]+/gi, "[토큰 제거]");
  const withoutJwt = withoutBearer.replace(
    /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g,
    "[토큰 제거]",
  );
  return withoutJwt.slice(0, maxLength);
}

// Only trusted route templates may leave the browser. Unknown/new routes fail closed
// until their template is added here; no user-controlled path segment is returned.
const CLIENT_SCREEN_TEMPLATES = `
/ /unknown /about /attendances /auth/callback /checkout /checkout/result /colleagues
/companies /companies/create /companies/:id /companies/:id/edit
/companies/:id/temp-wanted /companies/:id/temp-wanted/success
/company-register /company/:id/wanted-request /company/join
/dashboard /demo /duties /duty /duty-drafts /duty-generate /duty-requests
/duty-requests/new /duty-requests/:id /duty-requests/:id/edit /gpu-host
/help /help/:id /hiracare /hiracare/reports/:id /holidays /home
/inquiries /inquiries/new /inquiries/:id /intro /invalid-link
/login /login/email /login/forgot-password /member/:id /members/:id /my-info
/notices/app /notices/app/new /notices/app/:id/edit
/notices/dashboard /notices/dashboard/new /notices/dashboard/:id/edit
/nurses /nurses/create /nurses/:id /onboarding /opensource /positions /pricing /privacy
/pro-billing/refunds /pro-billing/refunds/:id /pro-data-deletion /reset-password
/s/d/:token /s/:token /pro/public/shares/:token
/schedules/new /schedules/:id /schedules/:id/history /schedules/:id/manage /schedules/:id/settings
/services/notices /services/notices/create /services/notices/:id/edit
/setting /setting/calendar /setting/my-hospital /setting/notification /setting/profile /setting/withdrawal
/settings/billing /settings/data-deletion /signup /signup-success
/statistics /statistics/duty-generation /statistics/duty-requests /statistics/user-trends /stats
/support /support/inquiries/:id /teams /teams/create /teams/:id
/terms /terms/new /terms/:id/edit /terms/marketing /terms/marketing-info /terms/opensource /terms/service
/trial-application /trial-applications /trial-applications/:id /users /users/:id
/wanted/history /wanted/request /wanteds /work-shifts /work-shifts/create /work-shifts/:id/edit
/jobs/:id /generation-jobs/:id /generation-notifications
`
  .trim()
  .split(/\s+/);
const STATIC_SCREENS = new Set(CLIENT_SCREEN_TEMPLATES.filter((path) => !path.includes(":")));
const DYNAMIC_SCREENS = CLIENT_SCREEN_TEMPLATES.filter((path) => path.includes(":")).map(
  (template) => ({ template, segments: template.split("/") }),
);

function hasUnsafePathCharacters(value: string) {
  return Array.from(value).some(
    (char) => char === "\\" || char.charCodeAt(0) <= 32 || char.charCodeAt(0) === 127,
  );
}

export function sanitizeClientScreen(screen?: string) {
  if (!screen) return "알 수 없는 화면";
  if (screen === "알 수 없는 화면") return screen;
  if (screen.length > 4096 || !screen.startsWith("/") || screen.startsWith("//")) return "/unknown";
  const path = screen.split(/[?#]/, 1)[0].replace(/\/$/, "") || "/";
  if (hasUnsafePathCharacters(path)) return "/unknown";
  let segments: string[];
  try {
    segments = path.split("/").map(decodeURIComponent);
  } catch {
    return "/unknown";
  }
  if (
    segments.some(
      (part, index) => index > 0 && (!part || part.includes("/") || hasUnsafePathCharacters(part)),
    )
  ) {
    if (path !== "/") return "/unknown";
  }
  // The public API share URL is accepted for legacy logging callers only.
  if (segments[1] === "api" && segments.slice(2, 5).join("/") === "pro/public/shares") {
    segments.splice(1, 1);
  }
  const normalized = segments.join("/");
  if (STATIC_SCREENS.has(normalized)) return normalized;
  return (
    DYNAMIC_SCREENS.find(
      ({ segments: template }) =>
        template.length === segments.length &&
        template.every((part, index) => part.startsWith(":") || part === segments[index]),
    )?.template ?? "/unknown"
  );
}

export function createPageViewParams(path: string | undefined, origin: string, service: string) {
  const safePath = sanitizeClientScreen(path);
  let safeOrigin = "";
  try {
    const url = new URL(origin);
    if (["http:", "https:"].includes(url.protocol) && !url.username && !url.password) {
      safeOrigin = url.origin;
    }
  } catch {
    // An invalid origin must never be copied verbatim into analytics.
  }
  return {
    service,
    page_path: safePath,
    page_location: safeOrigin ? `${safeOrigin}${safePath.startsWith("/") ? safePath : "/"}` : "",
    page_title: `${service} | ${safePath}`,
    page_referrer: "",
  };
}

// New analytics events need an explicit schema. Never forward arbitrary names,
// labels or extra fields: they can contain organization or personal information.
export function createClientAnalyticsEvent(
  eventName: string,
  params?: Readonly<Record<string, unknown>>,
) {
  if (eventName !== "duty_auto_generated") return undefined;
  const count = params?.duty_count;
  const mode = params?.mode === undefined ? "gpu" : params.mode;
  const source = params?.source === undefined ? "dashboard" : params.source;
  if (typeof count !== "number" || !Number.isSafeInteger(count) || count < 0) return undefined;
  if (mode !== "gpu" && mode !== "cpu" && mode !== "manual") return undefined;
  if (source !== "dashboard" && source !== "app") return undefined;
  return {
    name: "duty_auto_generated" as const,
    params: {
      event_category: "duty",
      event_label: "자동 생성 근무표 저장",
      value: count,
      duty_count: count,
      mode,
      source,
    },
  };
}

const REQUEST_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function sanitizeClientRequestId(requestId?: string) {
  if (!requestId || !REQUEST_ID_PATTERN.test(requestId)) return "없음";
  return requestId.toLowerCase();
}

export function createClientRequestId() {
  const cryptoApi = globalThis.crypto;
  if (!cryptoApi) return undefined;
  if (typeof cryptoApi.randomUUID === "function") return cryptoApi.randomUUID();

  const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"));
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex
    .slice(6, 8)
    .join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`;
}
